import { NavLink } from 'react-router';

import { useAppDispatch, useAppSelector } from '@app/hooks';
import { useInboxSummaryQuery } from '@shared/api/inboxSummaryApi';
import { BaseAvatar, BaseIconButton } from '@shared/components/base';
import { NAV_ITEMS, type NavItem } from '@shared/constants/navigation';
import { ACCESS_LEVEL_LABELS, type ConfigurablePage } from '@shared/constants/pages';
import { COMMON_TEXT } from '@shared/constants/text';
import { cn } from '@shared/lib/cn';
import { selectCurrentUser, selectPages } from '@shared/session/sessionSlice';

import { SHELL_TEXT } from '../constants';
import { toggleRail } from '../uiSlice';

import { ThemeSwitch } from './ThemeSwitch';

const ALWAYS_VISIBLE = new Set(['dashboard', 'myprofile']);

export function Sidebar({ onSignOut }: { onSignOut: () => void }) {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectCurrentUser);
  const pages = useAppSelector(selectPages);
  const { data: inbox } = useInboxSummaryQuery(undefined, { skip: !pages.notifs });
  const badge = inbox?.badge ?? 0;

  const visible = NAV_ITEMS.filter(
    (item) => ALWAYS_VISIBLE.has(item.page) || pages[item.page as ConfigurablePage],
  );
  // The prototype calls the dashboard "My Desk" for staff.
  const label = (item: NavItem) =>
    item.page === 'dashboard' && !user?.isAdmin ? SHELL_TEXT.sidebar.myDesk : item.label;

  return (
    <aside
      className="sticky top-0 flex h-screen flex-col gap-5 overflow-y-auto border-r border-line bg-surface px-3.5 py-5"
      aria-label={SHELL_TEXT.sidebar.mainNavigation}
    >
      {/* Name and the hide button side by side: one line normally; the name wraps onto a second
          line only when it would otherwise run under the button (larger text, other fonts). */}
      <div className="flex items-start gap-1.5 pl-1.5">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-[15px] leading-tight font-bold tracking-tight break-words">
            {COMMON_TEXT.brand.firmLegalName} {COMMON_TEXT.brand.appName}
          </span>
          <span className="mt-1.5 h-0.5 w-6.5 bg-brass" aria-hidden />
        </div>
        <BaseIconButton
          icon="pi pi-angle-double-left"
          size="sm"
          onClick={() => dispatch(toggleRail())}
          className="flex-none"
          aria-label={SHELL_TEXT.sidebar.hideSidebar}
          title={SHELL_TEXT.sidebar.hideSidebar}
        />
      </div>

      <nav className="flex flex-col gap-0.5">
        {visible.map((item) => (
          <NavLink
            key={item.page}
            to={item.path}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2.5 rounded-app px-2.5 py-2 text-[12.5px] text-text-2 no-underline',
                'hover:bg-surface-2 hover:text-text',
                isActive && 'bg-accent-soft font-semibold text-accent hover:bg-accent-soft hover:text-accent',
              )
            }
          >
            <i className={cn(item.icon, 'text-[13px]')} aria-hidden />
            <span className="flex-1">{label(item)}</span>
            {item.page === 'notifs' && badge > 0 && (
              <span
                className="inline-flex h-4 min-w-4 animate-badge-pulse items-center justify-center rounded-full bg-accent px-1.25 text-[10.5px] leading-4 font-bold text-on-accent motion-reduce:animate-none"
                aria-label={SHELL_TEXT.sidebar.unread(badge)}
              >
                {badge}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="mt-auto flex flex-col gap-3 px-1.5">
        {user && (
          <div className="flex items-center gap-2 border-t border-line pt-3">
            <BaseAvatar initials={user.initialsKey} />
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-[12px] font-semibold">{user.fullName}</span>
              <span className="text-[10.5px] text-text-3">{ACCESS_LEVEL_LABELS[user.accessLevel]}</span>
            </div>
            <BaseIconButton
              icon="pi pi-sign-out"
              variant="ghost"
              onClick={onSignOut}
              aria-label={SHELL_TEXT.sidebar.signOut}
              title={SHELL_TEXT.sidebar.signOut}
            />
          </div>
        )}
        <ThemeSwitch />
      </div>
    </aside>
  );
}
